import React from 'react';
import { View } from 'react-native';

import { BottomTabBarProps } from '@react-navigation/bottom-tabs';

import { AccountIcon } from '@/assets/icons/tab/account';
import PromohubIcon from '@/assets/icons/tab/promohub';
import { TabBar as TabBarModule } from '@/components/TabBar';
import { AccountStackTabs } from '@/feature/account-details/AccountStack';
import { PromoHubStackTabs } from '@/feature/promohub/PromohubStack';
import { ProductDocumentDataBodyTabsettingsSlice } from '@/types/prismic.generated';

import { ArcadeTabNavigatorParamList } from './arcade/types';
import { CasinoTabNavigatorParamList } from './casino/types';
import { PickemTabNavigatorParamList } from './pickem/types';
import { PredictionTabNavigatorParamList } from './prediction/types';
import { RipsTabNavigatorParamList } from './rips/types';
import { SweepsTabNavigatorParamList } from './sweeps/types';

export const TabBar = (props: BottomTabBarProps): React.JSX.Element => <TabBarModule {...props} />;

export const TabOptions = {
    headerShown: false,
    tabBarBackground: () => <View />,
    freezeOnBlur: true,
} as const;

export const PromosTabConfig = {
    name: 'PromohubStack',
    component: PromoHubStackTabs,
    icon: PromohubIcon,
    title: 'Promos',
} as const;

export const AccountTabConfig = {
    name: 'AccountStack',
    component: AccountStackTabs,
    icon: AccountIcon,
    title: 'Account',
} as const;

export type TabItem<
    T extends
        | keyof PickemTabNavigatorParamList
        | keyof SweepsTabNavigatorParamList
        | keyof ArcadeTabNavigatorParamList
        | keyof CasinoTabNavigatorParamList
        | keyof PredictionTabNavigatorParamList
        | keyof RipsTabNavigatorParamList
> = {
    name: T;
    component: () => React.ReactNode;
    icon: ({ focused, color, size }: { focused: boolean; color: string; size: number }) => React.ReactNode;
    title: string;
};

/**
 * Updates the tab configuration based on the provided Prismic tabSettings slice.
 *
 * @param tabs - The array of tab items to be updated.
 * @param tabBarSettings - Optional settings to customize tab titles and visibility.
 * @returns The updated array of tabs with titles overridden and hidden tabs filtered out.
 */
export const mapTabsFromTabSettings = <
    T extends
        | keyof PickemTabNavigatorParamList
        | keyof SweepsTabNavigatorParamList
        | keyof ArcadeTabNavigatorParamList
        | keyof CasinoTabNavigatorParamList
        | keyof PredictionTabNavigatorParamList
        | keyof RipsTabNavigatorParamList
>(
    tabs: TabItem<T>[],
    tabSettings?: ProductDocumentDataBodyTabsettingsSlice
): TabItem<T>[] => {
    if (!tabSettings) {
        return tabs;
    }

    return tabs
        .map(tab => {
            // Search for a corresponding tabname in the tabSettings slice
            const setting = tabSettings.items.find(tabSetting => tabSetting.tabname === tab.name);

            // If no corresponding setting is found, return the original tab
            if (!setting) {
                return tab;
            }

            // If hide is true, filter out (exclude) the tab
            if (setting.hide === true) {
                return null;
            }

            // If hide is false, override the title
            return {
                ...tab,
                title: setting.tablabel || tab.title, // Override title
            };
        })
        .filter(Boolean); // Exclude null values
};
